import {Raycaster, Vector2, Vector3, type Object3D} from 'three'

import {
  GROUND_OUTLIER_TOLERANCE_M,
  GROUND_SAMPLE_COUNT,
  MAX_PLACE_DISTANCE_M,
  MIN_PLACE_DISTANCE_M,
} from '../config'
import type {HitKind, PlacementHit, Xr8Facade} from '../engine/xr8-facade'
import {clientTo01, clientToNdc} from '../util/viewport'

/**
 * Preference order. A detected surface is real geometry from SLAM; an estimated
 * surface is the engine's floor guess; a feature point is a single tracked
 * point and is the least trustworthy.
 */
const PREFERENCE: readonly HitKind[] = ['DETECTED_SURFACE', 'ESTIMATED_SURFACE', 'FEATURE_POINT']

export type PlacementSource = HitKind | 'GROUND_FALLBACK'

export interface PlacementResult {
  readonly position: Vector3
  readonly source: PlacementSource
}

export interface HitTestService {
  /** Resolves a screen tap to a world position. */
  resolve(clientX: number, clientY: number): PlacementResult | null
  /**
   * Call once per frame while scanning to keep the ground estimate fresh.
   *
   * The median is what `isPlausible` measures feature points against, so this
   * has to keep being called before placement or the outlier filter silently
   * passes everything.
   *
   * Answers exactly what `resolve` would answer for a tap at the centre of the
   * screen, through the same code, rather than holding a second and stricter
   * opinion about where the floor is. `null` therefore means a tap there would
   * miss too (the centre is above the horizon, or further than
   * MAX_PLACE_DISTANCE_M).
   *
   * The return value has no consumer in the app itself - `?debug=1` reports it,
   * and `source` is the only way to tell "SLAM is blind" from "SLAM only has
   * feature points" on a device.
   */
  sampleGround(): PlacementResult | null
}

/** Median of a small array. Robust to the single outliers that sink models. */
const median = (values: readonly number[]): number | null => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[mid] ?? null
  const lo = sorted[mid - 1]
  const hi = sorted[mid]
  return lo === undefined || hi === undefined ? null : (lo + hi) / 2
}

export const createHitTestService = (
  facade: Xr8Facade,
  groundPlane: Object3D
): HitTestService => {
  const raycaster = new Raycaster()
  const ndc = new Vector2()
  const groundSamples: number[] = []

  const recordGroundSample = (y: number): void => {
    groundSamples.push(y)
    if (groundSamples.length > GROUND_SAMPLE_COUNT) groundSamples.shift()
  }

  const isPlausible = (hit: PlacementHit): boolean => {
    if (hit.distance < MIN_PLACE_DISTANCE_M || hit.distance > MAX_PLACE_DISTANCE_M) return false
    if (hit.kind !== 'FEATURE_POINT') return true

    // A feature point far from the established floor height is almost always a
    // point on a wall, a person, or noise. Placing on it drops the model
    // through the floor or hangs it in mid-air.
    const groundY = median(groundSamples)
    if (groundY === null) return true
    return Math.abs(hit.position.y - groundY) <= GROUND_OUTLIER_TOLERANCE_M
  }

  const pick = (hits: readonly PlacementHit[]): PlacementHit | null => {
    for (const kind of PREFERENCE) {
      const hit = hits.find((candidate) => candidate.kind === kind && isPlausible(candidate))
      if (hit) return hit
    }
    return null
  }

  /**
   * The one implementation of "where is the floor under this screen point".
   *
   * `resolve` and `sampleGround` ask the same question about different
   * coordinates, and used to answer it with two different bodies of code: the
   * tap path had the full preference order plus a geometric fallback, the
   * per-frame probe asked only for surface hits and had no fallback at all. The
   * two therefore disagreed about whether there was a floor at all, and the one
   * that shipped a visible feature was the weaker one. Keep them merged - the
   * split cannot be seen on desktop, because /debug never exercises this.
   *
   * Takes the probe point twice, in the two spaces it is needed in, rather than
   * converting here: the caller already holds whichever one it started from,
   * and converting a converted value is how the two drift apart.
   */
  const resolveAt = (
    feedX: number,
    feedY: number,
    ndcX: number,
    ndcY: number
  ): PlacementResult | null => {
    const hit = pick(facade.hitTest(feedX, feedY, PREFERENCE))
    if (hit) {
      // Feature points are deliberately kept out of the median that
      // `isPlausible` measures them against - seeding it with the very values
      // it exists to reject would let one bad point drag the floor with it.
      if (hit.kind !== 'FEATURE_POINT') recordGroundSample(hit.position.y)
      return {position: hit.position, source: hit.kind}
    }

    // SLAM has not converged yet, or the probe missed every tracked point.
    // Fall back to the geometric floor so a tap is never a dead tap.
    ndc.set(ndcX, ndcY)
    raycaster.setFromCamera(ndc, facade.camera)
    const intersection = raycaster.intersectObject(groundPlane, false)[0]
    if (!intersection) return null // above the horizon

    const distance = intersection.point.distanceTo(facade.camera.position)
    // Just below the horizon the intersection is near-infinite. Reject it
    // rather than placing the model hundreds of metres away.
    if (distance < MIN_PLACE_DISTANCE_M || distance > MAX_PLACE_DISTANCE_M) return null

    return {position: intersection.point.clone(), source: 'GROUND_FALLBACK'}
  }

  return {
    // The centre of the screen is 0.5, 0.5 in feed space and the origin in NDC.
    sampleGround: () => resolveAt(0.5, 0.5, 0, 0),

    resolve: (clientX, clientY) => {
      const canvas = facade.renderer.domElement
      // XRExtras.FullWindowCanvas makes the camera feed fill the canvas, so
      // canvas-relative coordinates map directly onto feed-relative ones. The
      // denominator is the canvas box and NOT the window - see util/viewport.ts
      // for why those are different quantities on iOS and on /debug.
      const feed = clientTo01(canvas, clientX, clientY)
      if (feed === null) return null
      // Same conversion for both spaces on purpose: the model tap-test in
      // src/interaction/model-hit.ts runs against the very same pointer event,
      // and two disagreeing conversions would show up only as a few pixels of
      // slop at the silhouette edge - undiagnosable in the field.
      if (clientToNdc(canvas, clientX, clientY, ndc) === null) return null
      return resolveAt(feed[0], feed[1], ndc.x, ndc.y)
    },
  }
}

/**
 * Yaw that turns an object at `position` to face `cameraPosition`, upright.
 *
 * The engine's hit rotations are ignored on purpose - feature-point normals are
 * noisy and produce visibly tilted characters.
 */
export const yawToFace = (position: Vector3, cameraPosition: Vector3): number =>
  Math.atan2(cameraPosition.x - position.x, cameraPosition.z - position.z)
